import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { Tooltip } from '@mui/material'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import PersonOffRoundedIcon from '@mui/icons-material/PersonOffRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded'
import { SkeletonRows } from 'components/Skeleton'
import { hideEmail } from 'utils/user'
import { RAG } from 'features/dashboard/charts/scoreColour'
import { ACTIVITY_STATUSES, activityStatus, lastActiveText, lastActiveTitle, lastActiveSortValue } from 'features/accessControl/lastActive'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'
import { ROLE_LABELS } from 'utils/roles'

const fullName = (u) => [u?.firstName, u?.lastName].filter(Boolean).join(' ') || (u?.email ? u.email.split('@')[0] : 'Unknown')
const initial = (u) => (fullName(u).charAt(0) || '?').toUpperCase()
const STATUS_ORDER = ['active', 'quiet', 'inactive', 'never']
const errorMessage = (err, fallback) => err?.response?.data?.message || fallback

const DomainRuleMembers = ({ api, grant, scopeName, scopeType, canManage, showActivity, onChanged }) => {
  const rulesWhere = scopeType === 'portfolio' ? `${scopeName} and the estates inside it` : scopeName
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('all')
  const [sort, setSort] = useState(() => (showActivity ? { key: 'activity', dir: 'desc' } : { key: 'name', dir: 'asc' }))
  const [confirmTarget, setConfirmTarget] = useState(null)
  const [pendingId, setPendingId] = useState(null)
  const [showRevoked, setShowRevoked] = useState(false)

  const load = useCallback(() => {
    setFailed(false)
    return api.getDomainGrantMembers(grant.id)
      .then((res) => setMembers(res?.data?.data || []))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }, [api, grant.id])

  useEffect(() => { load() }, [load])

  const statusOf = useCallback((m) => (showActivity ? activityStatus(m.lastActivityAt) : null), [showActivity])

  const withAccess = useMemo(() => members.filter((m) => !m.restricted), [members])
  const revoked = useMemo(() => members.filter((m) => m.restricted), [members])

  const counts = useMemo(() => {
    const c = { all: withAccess.length, active: 0, quiet: 0, inactive: 0, never: 0 }
    withAccess.forEach((m) => {
      const s = statusOf(m)
      if (s) c[s.key] += 1
    })
    return c
  }, [withAccess, statusOf])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    const rows = withAccess.filter((m) => {
      if (filter !== 'all' && statusOf(m)?.key !== filter) return false
      if (!q) return true
      return fullName(m).toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q)
    })
    const dir = sort.dir === 'asc' ? 1 : -1
    const byName = (a, b) => fullName(a).localeCompare(fullName(b))
    return [...rows].sort((a, b) => {
      if (sort.key === 'name') return dir * byName(a, b)
      if (sort.key === 'status') {
        const diff = STATUS_ORDER.indexOf(statusOf(a)?.key) - STATUS_ORDER.indexOf(statusOf(b)?.key)
        return diff !== 0 ? dir * diff : byName(a, b)
      }
      const diff = lastActiveSortValue(a.lastActivityAt) - lastActiveSortValue(b.lastActivityAt)
      return diff !== 0 ? dir * diff : byName(a, b)
    })
  }, [withAccess, query, filter, sort, statusOf])

  const toggleSort = (key) => setSort((prev) => (
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' }
  ))

  const setRestricted = (id, restricted) => setMembers((prev) => prev.map((m) => (
    m.id === id ? { ...m, restricted, restrictedAt: restricted ? new Date().toISOString() : null } : m
  )))

  const revoke = async () => {
    const person = confirmTarget
    if (!person) return
    setPendingId(person.id)
    try {
      const res = await api.restrictDomainUser(person.id)
      const lost = res?.data?.data?.revokedBpUsers ?? 0
      setRestricted(person.id, true)
      setConfirmTarget(null)
      toast.success(
        lost > 0
          ? `Access revoked for ${fullName(person)}. They lost access to ${lost} ${lost === 1 ? 'building' : 'buildings'}.`
          : `Access revoked for ${fullName(person)}. They can no longer get access through domain rules here.`
      )
      if (onChanged) onChanged()
    } catch (err) {
      toast.error(errorMessage(err, `Could not revoke access for ${fullName(person)}.`))
    } finally {
      setPendingId(null)
    }
  }

  const restore = async (person) => {
    setPendingId(person.id)
    try {
      await api.liftDomainRestriction(person.id)
      setRestricted(person.id, false)
      toast.success(`Access restored for ${fullName(person)}. Their access through the domain rule is back.`)
      if (onChanged) onChanged()
    } catch (err) {
      toast.error(errorMessage(err, `Could not restore ${fullName(person)}.`))
    } finally {
      setPendingId(null)
    }
  }

  if (loading) {
    return <Panel><SkeletonRows rows={3} gap={10} widths={['100%', '100%', '100%']} /></Panel>
  }

  if (failed) {
    return (
      <Panel>
        <Muted>
          Couldn’t load the people for this rule. <LinkBtn type='button' onClick={() => { setLoading(true); load() }}>Try again</LinkBtn>
        </Muted>
      </Panel>
    )
  }

  const revokedList = revoked.length > 0 && (
    <RevokedBlock>
      <RevokedToggle type='button' onClick={() => setShowRevoked((v) => !v)} aria-expanded={showRevoked}>
        {showRevoked ? <ExpandLessRoundedIcon style={{ fontSize: 18 }} /> : <ExpandMoreRoundedIcon style={{ fontSize: 18 }} />}
        Revoked access ({revoked.length})
      </RevokedToggle>
      {showRevoked && (
        <Rows>
          {revoked.map((m) => (
            <Row key={m.id} $activity={false} $restricted>
              <Person>
                <Avatar $restricted>{initial(m)}</Avatar>
                <Who>
                  <Name>
                    {fullName(m)}
                    <RestrictedTag><LockRoundedIcon style={{ fontSize: 11 }} /> Revoked</RestrictedTag>
                  </Name>
                  <Mail>{hideEmail(m.email)}</Mail>
                  {m.hasDirectAccess && <Note>Also added directly, so keeps that access</Note>}
                </Who>
              </Person>
              <Action>
                {canManage && (
                  <RestoreBtn type='button' onClick={() => restore(m)} disabled={pendingId === m.id}>
                    <RestoreRoundedIcon style={{ fontSize: 16 }} /> {pendingId === m.id ? 'Restoring…' : 'Restore access'}
                  </RestoreBtn>
                )}
              </Action>
            </Row>
          ))}
        </Rows>
      )}
    </RevokedBlock>
  )

  if (members.length === 0) {
    return (
      <Panel>
        <Muted>No one with an active account matches this domain yet. Anyone who joins later gets access automatically.</Muted>
      </Panel>
    )
  }

  const chips = [
    { key: 'all', label: 'Everyone' },
    ...(showActivity ? STATUS_ORDER.map((k) => ({ key: k, label: ACTIVITY_STATUSES[k].label, level: ACTIVITY_STATUSES[k].level })) : []),
  ].filter((c) => c.key === 'all' || c.key === filter || counts[c.key] > 0)

  const arrow = (key) => (sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '')

  return (
    <Panel>
      <Intro>
        Everyone with an account at @{grant.domain} gets access here. If someone no longer works there, revoke their access:
        they lose the access they had through domain rules on {rulesWhere}. People you added directly keep their access.
      </Intro>

      <Toolbar>
        <SearchBox>
          <SearchRoundedIcon style={{ fontSize: 17, color: ink.mute }} />
          <SearchInput
            type='search'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Search by name or email'
            aria-label='Search people covered by this domain rule'
          />
        </SearchBox>
        <Chips role='group' aria-label='Filter people'>
          {chips.map((c) => (
            <Chip key={c.key} type='button' $active={filter === c.key} onClick={() => setFilter(c.key)} aria-pressed={filter === c.key}>
              {c.level && <ChipDot style={{ background: RAG[c.level].fg }} />}
              {c.label} <ChipCount>{counts[c.key]}</ChipCount>
            </Chip>
          ))}
        </Chips>
      </Toolbar>

      <HeadRow $activity={showActivity}>
        <SortBtn type='button' onClick={() => toggleSort('name')} aria-label='Sort by name'>Person{arrow('name')}</SortBtn>
        {showActivity && <SortBtn type='button' onClick={() => toggleSort('activity')} aria-label='Sort by last active'>Last active{arrow('activity')}</SortBtn>}
        {showActivity && <SortBtn type='button' onClick={() => toggleSort('status')} aria-label='Sort by activity status'>Status{arrow('status')}</SortBtn>}
        <span />
      </HeadRow>

      {withAccess.length === 0 ? (
        <Muted>Everyone covered by this rule has had their access revoked.</Muted>
      ) : visible.length === 0 ? (
        <Muted>No one matches your search or filter.</Muted>
      ) : (
        <Rows>
          {visible.map((m) => {
            const status = statusOf(m)
            const busy = pendingId === m.id
            return (
              <Row key={m.id} $activity={showActivity}>
                <Person>
                  <Avatar>{initial(m)}</Avatar>
                  <Who>
                    <Name>{fullName(m)}</Name>
                    <Mail>{hideEmail(m.email)}</Mail>
                    {m.hasDirectAccess && <Note>Also added directly, so keeps that access</Note>}
                  </Who>
                </Person>
                {showActivity && (
                  <LastActive title={lastActiveTitle(m.lastActivityAt)} $never={!m.lastActivityAt}>
                    {lastActiveText(m.lastActivityAt)}
                  </LastActive>
                )}
                {showActivity && (
                  <Tooltip title={status.help} placement='top'>
                    <StatusChip style={{ color: RAG[status.level].fg, background: RAG[status.level].bg }}>
                      {status.label}
                    </StatusChip>
                  </Tooltip>
                )}
                <Action>
                  {canManage && (
                    <RestrictBtn type='button' onClick={() => setConfirmTarget(m)} disabled={busy}>
                      <BlockRoundedIcon style={{ fontSize: 16 }} /> Revoke access
                    </RestrictBtn>
                  )}
                </Action>
              </Row>
            )
          })}
        </Rows>
      )}

      {revokedList}

      <Footer>
        Showing {visible.length} of {withAccess.length} {withAccess.length === 1 ? 'person' : 'people'} with access
        {!showActivity && ` · Only ${ROLE_LABELS.owner}s can see when people were last active.`}
      </Footer>

      {confirmTarget && createPortal(
        <Overlay onClick={() => pendingId === null && setConfirmTarget(null)}>
          <Card onClick={(e) => e.stopPropagation()} role='dialog' aria-modal='true' aria-labelledby='restrict-title'>
            <CardHead>
              <WarnIcon><PersonOffRoundedIcon style={{ fontSize: 20 }} /></WarnIcon>
              <div>
                <CardTitle id='restrict-title'>Revoke access for {fullName(confirmTarget)}?</CardTitle>
                <CardSub>{hideEmail(confirmTarget.email)}</CardSub>
              </div>
            </CardHead>
            <CardBody>
              They will lose the access they had through domain rules on {rulesWhere}, on every building here.
              {confirmTarget.hasDirectAccess ? ' They were also added directly, so that access stays.' : ' Access you gave them directly is not affected.'}
              {' '}You can restore them at any time.
            </CardBody>
            <CardActions>
              <CancelBtn type='button' onClick={() => setConfirmTarget(null)} disabled={pendingId !== null}>Cancel</CancelBtn>
              <ConfirmBtn type='button' onClick={revoke} disabled={pendingId !== null}>
                {pendingId !== null ? 'Revoking…' : 'Revoke access'}
              </ConfirmBtn>
            </CardActions>
          </Card>
        </Overlay>,
        document.body
      )}
    </Panel>
  )
}

export default DomainRuleMembers

const Panel = styled.div`display:flex;flex-direction:column;gap:10px;padding:10px 12px 12px;border-top:1px dashed ${line.base};`
const Muted = styled.div`font-size:${font.small};color:${ink.mute};padding:6px 2px;line-height:1.5;`
const LinkBtn = styled.button`background:none;border:none;padding:0;font-size:inherit;font-weight:700;color:${({ theme }) => theme.colors.secondary};cursor:pointer;text-decoration:underline;`
const RevokedBlock = styled.div`display:flex;flex-direction:column;gap:8px;padding-top:4px;`
const RevokedToggle = styled.button`
  align-self:flex-start;display:inline-flex;align-items:center;gap:4px;min-height:40px;padding:0 8px;
  background:none;border:none;border-radius:${radius.sm};font-size:${font.small};font-weight:700;color:${ink.soft};cursor:pointer;
  &:hover{background:${surface.wash};color:${ink.base};}
  &:focus-visible{outline:2px solid ${({ theme }) => theme.colors.secondary};outline-offset:2px;}
`
const Intro = styled.p`margin:0;font-size:${font.small};color:${ink.soft};line-height:1.55;max-width:72ch;`
const Toolbar = styled.div`display:flex;flex-wrap:wrap;align-items:center;gap:10px;`
const SearchBox = styled.label`
  flex:1 1 220px;display:flex;align-items:center;gap:6px;padding:0 10px;min-height:40px;
  border:1px solid ${line.strong};border-radius:${radius.sm};background:${surface.card};
  &:focus-within{border-color:${({ theme }) => theme.colors.secondary};}
`
const SearchInput = styled.input`flex:1;min-width:0;border:none;outline:none;background:transparent;font-size:${font.body};color:${ink.base};`
const Chips = styled.div`display:flex;flex-wrap:wrap;gap:6px;`
const Chip = styled.button`
  display:inline-flex;align-items:center;gap:6px;min-height:34px;padding:0 12px;border-radius:${radius.pill};cursor:pointer;
  font-size:${font.small};font-weight:700;transition:${transition};
  border:1px solid ${({ $active, theme }) => ($active ? theme.colors.secondary : line.strong)};
  background:${({ $active, theme }) => ($active ? `${theme.colors.secondary}14` : surface.card)};
  color:${({ $active, theme }) => ($active ? theme.colors.secondary : ink.soft)};
  &:hover{border-color:${({ theme }) => theme.colors.secondary};}
`
const ChipDot = styled.span`width:8px;height:8px;border-radius:50%;flex-shrink:0;`
const ChipCount = styled.span`font-variant-numeric:tabular-nums;font-weight:600;opacity:0.8;`
const grid = ({ $activity }) => ($activity ? 'minmax(0,1fr) 150px 130px 150px' : 'minmax(0,1fr) 150px')
const HeadRow = styled.div`
  display:grid;grid-template-columns:${grid};gap:12px;padding:0 8px;
  @media (max-width:${bpToken.mobile}){display:none;}
`
const SortBtn = styled.button`
  background:none;border:none;padding:4px 0;text-align:left;cursor:pointer;
  font-size:11px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:${ink.mute};
  &:hover{color:${ink.base};}
`
const Rows = styled.div`display:flex;flex-direction:column;gap:4px;`
const Row = styled.div`
  display:grid;grid-template-columns:${grid};gap:12px;align-items:center;padding:8px;border-radius:${radius.sm};
  background:${({ $restricted }) => ($restricted ? surface.wash : 'transparent')};
  @media (max-width:${bpToken.mobile}){grid-template-columns:1fr auto;row-gap:8px;}
`
const Person = styled.div`display:flex;align-items:center;gap:10px;min-width:0;@media (max-width:${bpToken.mobile}){grid-column:1 / -1;}`
const Avatar = styled.span`
  flex-shrink:0;width:32px;height:32px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;
  font-size:13px;font-weight:800;background:${line.base};color:${({ $restricted }) => ($restricted ? ink.mute : ink.soft)};
`
const Who = styled.div`min-width:0;display:flex;flex-direction:column;`
const Name = styled.span`display:flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:${font.body};font-weight:600;color:${ink.base};`
const Mail = styled.span`font-size:${font.caption};color:${ink.mute};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;`
const Note = styled.span`font-size:${font.caption};color:${ink.soft};margin-top:2px;`
const RestrictedTag = styled.span`
  display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:700;padding:2px 8px;border-radius:${radius.pill};
  background:${RAG.none.bg};color:${RAG.none.fg};border:1px solid ${line.strong};
`
const LastActive = styled.span`font-size:${font.small};font-weight:600;color:${({ $never }) => ($never ? ink.mute : ink.soft)};cursor:help;`
const StatusChip = styled.span`justify-self:start;font-size:12px;font-weight:700;padding:4px 10px;border-radius:${radius.pill};white-space:nowrap;cursor:help;`
const Action = styled.div`display:flex;justify-content:flex-end;@media (max-width:${bpToken.mobile}){grid-column:1 / -1;justify-content:stretch;}`
const actionBtn = `
  display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:40px;padding:0 14px;border-radius:${radius.sm};
  font-size:13px;font-weight:700;cursor:pointer;transition:${transition};white-space:nowrap;
  &:disabled{opacity:0.6;cursor:default;}
  @media (max-width:${bpToken.mobile}){width:100%;}
`
const RestrictBtn = styled.button`
  ${actionBtn}
  border:1px solid ${RAG.critical.fg}55;background:${surface.card};color:${RAG.critical.fg};
  &:hover:not(:disabled){background:${RAG.critical.bg};}
`
const RestoreBtn = styled.button`
  ${actionBtn}
  border:1px solid ${({ theme }) => theme.colors.secondary};background:${surface.card};color:${({ theme }) => theme.colors.secondary};
  &:hover:not(:disabled){background:${({ theme }) => `${theme.colors.secondary}14`};}
`
const Footer = styled.div`font-size:${font.caption};color:${ink.mute};padding:2px 2px 0;`
const Overlay = styled.div`
  position:fixed;inset:0;z-index:100010;background:rgba(15,23,42,0.45);
  display:flex;align-items:center;justify-content:center;padding:16px;
`
const Card = styled.div`
  width:100%;max-width:460px;max-height:88vh;overflow:auto;background:${surface.card};
  border:1px solid ${line.base};border-radius:${radius.md};box-shadow:0 12px 40px rgba(15,23,42,0.18);
`
const CardHead = styled.div`display:flex;align-items:flex-start;gap:12px;padding:18px 20px 6px;`
const WarnIcon = styled.span`
  flex-shrink:0;width:36px;height:36px;border-radius:${radius.sm};display:inline-flex;align-items:center;justify-content:center;
  background:${RAG.critical.bg};color:${RAG.critical.fg};
`
const CardTitle = styled.h3`margin:0;font-size:16px;font-weight:800;color:${ink.base};line-height:1.3;`
const CardSub = styled.div`font-size:${font.caption};color:${ink.mute};margin-top:2px;`
const CardBody = styled.div`padding:8px 20px 4px;font-size:${font.body};color:${ink.soft};line-height:1.55;`
const CardActions = styled.div`display:flex;justify-content:flex-end;gap:10px;padding:14px 20px 18px;`
const CancelBtn = styled.button`
  min-height:40px;padding:0 16px;border-radius:${radius.sm};border:1px solid ${line.strong};background:${surface.card};
  color:${ink.soft};font-size:13px;font-weight:700;cursor:pointer;transition:${transition};
  &:hover:not(:disabled){background:${surface.wash};}&:disabled{opacity:0.6;cursor:default;}
`
const ConfirmBtn = styled.button`
  min-height:40px;padding:0 16px;border-radius:${radius.sm};border:1px solid ${RAG.critical.fg};background:${RAG.critical.fg};
  color:#fff;font-size:13px;font-weight:700;cursor:pointer;transition:${transition};
  &:hover:not(:disabled){filter:brightness(0.92);}&:disabled{opacity:0.7;cursor:default;}
`
